import { matchPath } from 'react-router-dom';

/**
 * Does `pathname` belong to the mobile app?
 *
 * Pure so it can be tested without standing up the shell, and so the
 * allowlist stays one readable rule: a path is in the mobile app only if it
 * matches a route pattern owned by a tab this user can actually see. Matching
 * is exact (`end: true`) — `/requests` owning `/requests/:id` is deliberate and
 * spelled out, but it must not silently own `/requests/:id/settings` too.
 */
export function isMobileAppPath(pathname: string, patterns: string[]): boolean {
  return patterns.some(pattern => !!matchPath({ path: pattern, end: true }, pathname));
}

/**
 * Should the shell bounce this path to Home?
 *
 * The allowlist is built from feature flags and role gates that both default
 * to false and resolve a beat after mount. Asking "is this path allowed?" too
 * early answers "no" for every route but Home — which, with a `replace`
 * navigation, silently destroys deep links from email (/requests/<id>,
 * /ellie/conversation/<id>, /knowledge-base/article/<id>) on cold load. The
 * back button can't even recover them.
 *
 * So the question isn't "is it allowed" but "do we yet know that it isn't".
 * Until the inputs resolve there is no answer, and no answer must never mean
 * redirect. `isResolved` is what separates "not allowed" from "not known yet";
 * the shell renders the route in the meantime.
 */
export function shouldRedirectToHome({
  pathname,
  patterns,
  isResolved,
}: {
  pathname: string;
  patterns: string[];
  isResolved: boolean;
}): boolean {
  if (!isResolved) return false;
  return !isMobileAppPath(pathname, patterns);
}
